import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '../../../../services/api'
import type { FranchisePartnerResource } from '../../../../types/resources'

export interface FranchisePartner {
  id: number
  fp_code: string
  franchise_name: string
  country: string
  state: string
  market?: string
  region_code?: string
  region_name?: string
  bdm_id?: number
  bdm_name?: string
  system_status: string
  fp_status: string
  is_icu: boolean
  is_selling: boolean
  adoption_status: string | null
  adoption_note: string | null
  start_date: string | null
  field_management_system: string
  lightspeed_store_code?: string
  lightspeed_domain?: string
  lightspeed_url?: string
  xero_short_code?: string
  resources?: FranchisePartnerResource[]
  outlet_count?: number
  // Data counts from backend
  products_count?: number
  sales_count?: number
  clients_count?: number
  // Xero data counts
  xero_invoices_count?: number
  xero_payments_count?: number
  // Total data count
  total_data_count?: number
  // Last sync tracking fields (overall)
  last_sync_at?: string
  last_sync_system?: string
  last_sync_type?: string
  // Per-system sync times
  lightspeed_last_sync_at?: string
  xero_last_sync_at?: string
  skimmer_last_sync_at?: string
  // Per-system record counts
  lightspeed_record_count?: number
  xero_record_count?: number
  skimmer_record_count?: number
  // Connection status fields
  has_complete_lightspeed_connection?: boolean
  has_complete_xero_connection?: boolean
  has_complete_skimmer_connection?: boolean
  // FP User access
  manager_count?: number
  manager_names?: string[]
  manager_latest_login?: string
}

interface UseFranchisePartnersDataReturn {
  franchisePartners: FranchisePartner[]
  loading: boolean
  error: string | null
  refetch: () => void
  updatePartner: (fpCode: string, updates: Partial<FranchisePartner>) => void
}

export const useFranchisePartnersData = (): UseFranchisePartnersDataReturn => {
  const [franchisePartners, setFranchisePartners] = useState<FranchisePartner[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchFranchisePartners = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiClient.get('/franchise-partners?enabled_only=false')
      setFranchisePartners(response.data.partners || [])
      setError(null)
    } catch (err) {
      setError('Failed to fetch franchise partners')
      console.error('Error fetching franchise partners:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const updatePartner = useCallback((fpCode: string, updates: Partial<FranchisePartner>) => {
    setFranchisePartners(prevFps =>
      prevFps.map(fp =>
        fp.fp_code === fpCode
          ? { ...fp, ...updates }
          : fp
      )
    )
  }, [])

  useEffect(() => {
    fetchFranchisePartners()
  }, [fetchFranchisePartners])

  return {
    franchisePartners,
    loading,
    error,
    refetch: fetchFranchisePartners,
    updatePartner
  }
}